import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Label } from "./components/ui/label";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";
import { Checkbox } from "./components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./components/ui/collapsible";
import { Button } from "./components/ui/button";
import ZugferdInfo from "./BillingInfo";

export const BillingSection = ({ t, formData, setFormData, validationErrors }) => {
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      billing: {
        ...prev.billing,
        [name]: type === 'checkbox' ? e.target.checked : value
      }
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({
      ...prev,
      billing: {
        ...prev.billing,
        [name]: checked
      }
    }));
  };

  const handleDeliveryMethodChange = (value) => {
    setFormData(prev => ({
      ...prev,
      billing: {
        ...prev.billing,
        deliveryMethod: value
      }
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.billing.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label>{t.billing.billingEmail}</Label>
            <Input
              name="billingEmail"
              type="email"
              value={formData.billing.billingEmail}
              onChange={handleChange}
              className={validationErrors['billing.billingEmail'] ? 'border-red-500' : ''}
            />
            {validationErrors['billing.billingEmail'] && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors['billing.billingEmail']}
              </p>
            )}
          </div>

          <div className="bg-gray-50 p-4 rounded-md">
            <ZugferdInfo 
              info={t.billing.standardDeliveryInfo}
              details={t.billing.zugferdDetails}
            />
            
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm">
                  {t.billing.changeDeliveryMethod}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4">
                <RadioGroup
                  value={formData.billing.deliveryMethod}
                  onValueChange={handleDeliveryMethodChange}
                  className="space-y-2"
                >
                  {Object.entries(t.billing.deliveryOptions).map(([value, label]) => (
                    <div key={value} className="flex items-center space-x-2">
                      <RadioGroupItem value={value} id={value} />
                      <Label htmlFor={value}>{label}</Label>
                    </div>
                  ))}
                </RadioGroup>

                {formData.billing.deliveryMethod === 'xInvoice' && (
                  <div className="mt-4">
                    <Label>{t.billing.routingId}</Label>
                    <Input
                      name="routingId"
                      value={formData.billing.routingId}
                      onChange={handleChange}
                    />
                  </div>
                )}

                {formData.billing.deliveryMethod === 'portal' && (
                  <div className="mt-4">
                    <Label>{t.billing.portalUrl}</Label>
                    <Input
                      name="portalUrl"
                      value={formData.billing.portalUrl}
                      onChange={handleChange}
                    />
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
          
        {/* Additional Information */}
        <div>
          <h3 className="font-bold text-lg mb-4">{t.billing.additionalInfo}</h3>
          <div className="space-y-4">
            {/* Purchase Order */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="poRequired"
                  checked={formData.billing.poRequired}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('poRequired', checked)
                  }
                />
                <Label htmlFor="poRequired">{t.billing.poRequired}</Label>
              </div>
              {formData.billing.poRequired && (
                <div className="ml-6">
                  <Input
                    name="poNumber"
                    value={formData.billing.poNumber}
                    onChange={handleChange}
                    placeholder={t.billing.poNumber}
                  />
                </div>
              )}
            </div>

            {/* Project Reference */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="projectRefRequired"
                  checked={formData.billing.projectRefRequired}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('projectRefRequired', checked)
                  }
                />
                <Label htmlFor="projectRefRequired">{t.billing.projectRef}</Label>
              </div>
              {formData.billing.projectRefRequired && (
                <div className="ml-6">
                  <Input
                    name="projectId"
                    value={formData.billing.projectId}
                    onChange={handleChange}
                    placeholder={t.billing.projectId}
                  />
                </div>
              )}
            </div>

            {/* Cost Center */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="costCenterRequired"
                  checked={formData.billing.costCenterRequired}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('costCenterRequired', checked)
                  }
                />
                <Label htmlFor="costCenterRequired">{t.billing.costCenter}</Label>
              </div>
              {formData.billing.costCenterRequired && (
                <div className="ml-6">
                  <Input
                    name="costCenterNumber"
                    value={formData.billing.costCenterNumber}
                    onChange={handleChange}
                    placeholder={t.billing.costCenterNumber}
                  />
                </div>
              )}
            </div>

            {/* Other Requirements */}
            <div>
              <Label>{t.billing.otherRequirements}</Label>
              <Textarea
                name="otherRequirements"
                value={formData.billing.otherRequirements}
                onChange={handleChange}
                placeholder={t.billing.otherRequirementsPlaceholder}
                rows={3}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};